// Schémas Zod partagés entre le serveur (validation des entrées) et l'interface (formulaires, types).

import { z } from 'zod';
import {
  ACTIONS,
  CATEGORIES_INSTALLATIONS,
  CODES_PROVINCES,
  DENSITES_AFFICHAGE,
  TAILLES_TEXTE,
  ETATS_VALIDATION,
  FONCTIONNALITES,
  NIVEAUX_CONFIDENTIALITE,
  RESSOURCES,
  ROLES,
  SOURCES_DONNEES,
  THEMES,
  TYPES_OBJETS_DOCUMENTS,
  TYPES_ORGANISATIONS,
  TYPES_RELATIONS_ORGANISATIONS,
  UNITES,
} from '../constantes';

export const identifiant = z.uuid({ message: 'Identifiant invalide' });
export const chaineDecimale = z
  .string()
  .regex(/^-?\d+(\.\d+)?$/, { message: 'Valeur décimale attendue (point comme séparateur)' });
export const chaineDecimalePositive = z
  .string()
  .regex(/^\d+(\.\d+)?$/, { message: 'Valeur décimale positive attendue (point comme séparateur)' });
export const texteCourt = z.string().trim().min(1, { message: 'Champ obligatoire' }).max(200);
export const motif = z.string().trim().min(3, { message: 'Un motif est obligatoire' }).max(1000);
export const dateIso = z.iso.datetime({ offset: true, message: 'Date et heure ISO 8601 attendues' });
export const longitude = z.number().min(-180).max(180);
export const latitude = z.number().min(-90).max(90);

export const schemaRole = z.enum(ROLES);
export const schemaTypeOrganisation = z.enum(TYPES_ORGANISATIONS);
export const schemaCategorieInstallation = z.enum(CATEGORIES_INSTALLATIONS);
export const schemaConfidentialite = z.enum(NIVEAUX_CONFIDENTIALITE);
export const schemaEtatValidation = z.enum(ETATS_VALIDATION);
export const schemaUnite = z.enum(UNITES);
export const schemaAction = z.enum(ACTIONS);
export const schemaRessource = z.enum(RESSOURCES);
export const schemaSource = z.enum(SOURCES_DONNEES);
export const schemaCodeProvince = z.enum(CODES_PROVINCES);
export const schemaTheme = z.enum(THEMES);

// Organisations ---------------------------------------------------------------------------------

export const schemaCreationOrganisation = z.object({
  nom: texteCourt,
  sigle: z.string().trim().max(40).optional().nullable(),
  type: schemaTypeOrganisation,
  estFictive: z.boolean().default(false),
  provinceCode: schemaCodeProvince.optional().nullable(),
  adresse: z.string().trim().max(500).optional().nullable(),
  telephone: z.string().trim().max(40).optional().nullable(),
  courriel: z.email().optional().nullable(),
});
export type CreationOrganisation = z.infer<typeof schemaCreationOrganisation>;

export const schemaModificationOrganisation = schemaCreationOrganisation.partial().extend({ motif: motif.optional() });

export const schemaCreationRelation = z.object({
  organisationSourceId: identifiant,
  organisationCibleId: identifiant,
  type: z.enum(TYPES_RELATIONS_ORGANISATIONS),
  dateDebut: dateIso.optional(),
  dateFin: dateIso.optional().nullable(),
});

// Utilisateurs et affectations -------------------------------------------------------------------

export const schemaPerimetre = z.object({
  provinces: z.array(schemaCodeProvince).default([]),
  departements: z.array(identifiant).default([]),
  installations: z.array(identifiant).default([]),
});
export type Perimetre = z.infer<typeof schemaPerimetre>;

export const schemaCreationAffectation = z.object({
  organisationId: identifiant,
  role: schemaRole,
  perimetre: schemaPerimetre.default({ provinces: [], departements: [], installations: [] }),
  dateDebut: dateIso.optional(),
  dateFin: dateIso.optional().nullable(),
  motif: motif.optional(),
});
export type CreationAffectation = z.infer<typeof schemaCreationAffectation>;

export const schemaCreationUtilisateur = z.object({
  courriel: z.email({ message: 'Adresse de courriel invalide' }).transform((c) => c.toLowerCase()),
  prenom: texteCourt,
  nom: texteCourt,
  telephone: z.string().trim().max(40).optional().nullable(),
  fonction: z.string().trim().max(200).optional().nullable(),
  affectations: z.array(schemaCreationAffectation).min(1, { message: 'Au moins une affectation est requise' }),
});
export type CreationUtilisateur = z.infer<typeof schemaCreationUtilisateur>;

export const schemaModificationUtilisateur = z.object({
  prenom: texteCourt.optional(),
  nom: texteCourt.optional(),
  telephone: z.string().trim().max(40).optional().nullable(),
  fonction: z.string().trim().max(200).optional().nullable(),
});

export const schemaActionCompte = z.object({ motif });

// Authentification -------------------------------------------------------------------------------

export const schemaConnexion = z.object({
  courriel: z.email({ message: 'Adresse de courriel invalide' }).transform((c) => c.toLowerCase()),
  motDePasse: z.string().min(1, { message: 'Mot de passe obligatoire' }).max(256),
  /** Connexion depuis un bouton de compte de présentation (D-037) ; sans effet hors environnement de présentation. */
  presentation: z.boolean().optional(),
});

export const schemaCodeMfa = z.object({
  code: z
    .string()
    .trim()
    .regex(/^(\d{6}|[a-z0-9]{4}-[a-z0-9]{4})$/i, { message: 'Code à six chiffres ou code de secours attendu' }),
});

export const schemaChangementMotDePasse = z.object({
  motDePasseActuel: z.string().min(1).max(256),
  nouveauMotDePasse: z.string().min(1).max(256),
});

// Permissions -------------------------------------------------------------------------------------

export const schemaPermission = z.object({
  role: schemaRole,
  action: schemaAction,
  ressource: schemaRessource,
  niveauMax: schemaConfidentialite,
});
export type Permission = z.infer<typeof schemaPermission>;

export const schemaModificationPermissions = z.object({
  permissions: z.array(schemaPermission.omit({ role: true })),
  motif,
});

// Paramètres --------------------------------------------------------------------------------------

export const schemaPortee = z.object({
  type: z.enum(['nationale', 'province', 'type_installation', 'produit', 'organisation']),
  valeur: z.string().trim().max(100).nullable().default(null),
});

export const schemaModificationParametre = z.object({
  valeur: z.unknown(),
  portee: schemaPortee.default({ type: 'nationale', valeur: null }),
  dateEffet: dateIso.optional(),
  motif,
});
export type ModificationParametre = z.infer<typeof schemaModificationParametre>;

export const schemaFonctionnalite = z.enum(FONCTIONNALITES);

// Territoires et référentiel ---------------------------------------------------------------------

export const schemaCreationLocalite = z.object({
  nom: texteCourt,
  type: z.enum(['ville', 'commune', 'village', 'quartier']),
  longitude,
  latitude,
  departementId: identifiant.optional().nullable(),
});

export const schemaCreationInstallation = z.object({
  nom: texteCourt,
  categorie: schemaCategorieInstallation,
  organisationId: identifiant.optional(),
  longitude,
  latitude,
  localiteId: identifiant.optional().nullable(),
  adresse: z.string().trim().max(500).optional().nullable(),
  confidentialite: schemaConfidentialite.default('restreinte'),
  horaires: z.string().trim().max(200).optional().nullable(),
});
export type CreationInstallation = z.infer<typeof schemaCreationInstallation>;

export const schemaModificationInstallation = schemaCreationInstallation
  .omit({ organisationId: true })
  .partial()
  .extend({ motif: motif.optional() });

export const schemaRattachement = z.object({
  provinceCode: schemaCodeProvince,
  departementId: identifiant.nullable(),
  motif,
});

export const schemaDecisionValidation = z.object({
  decision: z.enum(['valider', 'rejeter']),
  motif: z.string().trim().max(1000).optional(),
});

export const schemaFusion = z.object({ cibleId: identifiant, motif });

/** Écartement d'une paire de doublons potentiels : ce ne sont pas des doublons (motif obligatoire). */
export const schemaEcartementDoublon = z.object({ installationAId: identifiant, installationBId: identifiant, motif });

export const schemaCreationReservoir = z.object({
  installationId: identifiant,
  numero: texteCourt,
  produitCode: z.string().trim().min(1).max(40),
  capaciteNominale: chaineDecimalePositive,
  volumeNonPompable: chaineDecimalePositive.default('0'),
  unite: z.enum(['L', 'kg']).default('L'),
});

export const schemaCreationVehicule = z.object({
  organisationId: identifiant.optional(),
  immatriculation: z.string().trim().min(3).max(20),
  type: z.enum(['citerne', 'porteur_bouteilles', 'wagon_citerne', 'barge']),
  capaciteLitres: chaineDecimalePositive,
  compartiments: z.number().int().min(1).max(12).default(1),
});

export const schemaCreationConducteur = z.object({
  organisationId: identifiant.optional(),
  prenom: texteCourt,
  nom: texteCourt,
  telephone: z.string().trim().max(40).optional().nullable(),
  numeroPermis: z.string().trim().max(40).optional().nullable(),
});

// Catalogue ---------------------------------------------------------------------------------------

export const schemaCreationProduit = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9_]{2,40}$/),
  nom: texteCourt,
  uniteReference: schemaUnite,
  estGpl: z.boolean().default(false),
});

export const schemaCreationConversion = z.object({
  produitCode: z.string().trim().min(1).max(40),
  parametre: z.enum(['densite_kg_par_litre']),
  valeur: chaineDecimalePositive,
  dateEffet: dateIso.optional(),
  motif,
});

export const schemaCreationFormatBouteille = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9_]{2,20}$/),
  libelle: texteCourt,
  contenuKg: chaineDecimalePositive,
  produitCode: z.string().trim().min(1).max(40).default('gpl_butane'),
});

// Documents ---------------------------------------------------------------------------------------

export const schemaMetadonneesDocument = z.object({
  titre: texteCourt,
  objetType: z.enum(TYPES_OBJETS_DOCUMENTS),
  objetId: identifiant,
  confidentialite: schemaConfidentialite.default('restreinte'),
  categorie: z.string().trim().max(80).optional().nullable(),
});

// Préférences -------------------------------------------------------------------------------------

export const schemaPreferences = z.object({
  theme: schemaTheme,
  densite: z.enum(DENSITES_AFFICHAGE),
  tailleTexte: z.enum(TAILLES_TEXTE).default('normale'),
  notifications: z.object({
    interne: z.boolean(),
    courriel: z.boolean(),
    sms: z.boolean(),
    niveauMinimal: z.enum(['informative', 'preventive', 'elevee', 'critique']),
  }),
});
export type Preferences = z.infer<typeof schemaPreferences>;

export const PREFERENCES_DEFAUT: Preferences = {
  theme: 'systeme',
  densite: 'confortable',
  tailleTexte: 'normale',
  notifications: { interne: true, courriel: false, sms: false, niveauMinimal: 'preventive' },
};

export const schemaVueEnregistree = z.object({
  nom: texteCourt,
  ecran: z.string().trim().min(1).max(80),
  filtres: z.record(z.string(), z.unknown()),
});

export const schemaPagination = z.object({
  curseur: z.string().max(200).optional(),
  limite: z.coerce.number().int().min(1).max(200).default(50),
});
